// Apolo IDE — ChartBlockBody (sprint plan-charts).
//
// Bloco de gráfico no canvas. Toggle Dados↔Gráfico. Tipo line/bar/area/pie.
// Dados manuais (minitabela label/valor) OU preset do plano (dispara evento
// apolo:plan:chartPreset; o PlanCanvas computa via buildPresetSeries e faz
// patch — ele tem os blocks + estimativas). Render via ChartRenderer (lazy).

import { useEffect, useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import ChartRenderer from '../ChartRenderer.jsx';
import { CHART_TYPES, PRESETS } from '../../../lib/plan/chartData.js';

export default function ChartBlockBody({ block, payload, onChange }) {
  const type = CHART_TYPES.includes(payload.type) ? payload.type : 'line';
  const series = Array.isArray(payload.series) && payload.series.length ? payload.series : [{ name: 'Série', data: [] }];
  const data = series[0].data || [];
  const hasData = data.length > 0;
  const [mode, setMode] = useState(hasData ? 'chart' : 'data');
  const [presetOpen, setPresetOpen] = useState(false);
  const presetRef = useRef(null);

  useEffect(() => {
    if (!presetOpen) return undefined;
    function out(e) { if (presetRef.current && !presetRef.current.contains(e.target)) setPresetOpen(false); }
    document.addEventListener('mousedown', out);
    return () => document.removeEventListener('mousedown', out);
  }, [presetOpen]);

  function update(patch) { onChange({ ...payload, type, series, ...patch }); }
  function setPoint(i, key, val) {
    const next = data.map((d, k) => (k === i ? { ...d, [key]: key === 'y' ? (val === '' ? null : Number(val)) : val } : d));
    update({ series: [{ ...series[0], data: next }], source: 'manual' });
  }
  function addRow() { update({ series: [{ ...series[0], data: [...data, { x: '', y: null }] }], source: 'manual' }); }
  function removeRow(i) { update({ series: [{ ...series[0], data: data.filter((_, k) => k !== i) }], source: 'manual' }); }

  function applyPreset(presetId) {
    // PlanCanvas escuta, computa buildPresetSeries (tem blocks+estimates) e
    // faz patch deste bloco. Aqui só dispara.
    window.dispatchEvent(new CustomEvent('apolo:plan:chartPreset', { detail: { blockId: block.id, presetId } }));
    setPresetOpen(false);
    setMode('chart');
  }

  return (
    <div className="flex h-full w-full flex-col">
      {/* Toolbar do bloco */}
      <div className="flex items-center gap-1 border-b border-apolo-borderSubtle/60 px-2 py-1.5">
        <select
          value={type}
          onChange={(e) => update({ type: e.target.value })}
          aria-label="Tipo de gráfico"
          className="rounded border border-apolo-borderSubtle bg-apolo-bg px-1.5 py-0.5 text-[11px] text-apolo-textPrimary focus:border-apolo-plan-accentBorder focus:outline-none"
        >
          <option value="line">Linha</option>
          <option value="bar">Barra</option>
          <option value="area">Área</option>
          <option value="pie">Pizza</option>
        </select>
        <input
          type="text"
          value={payload.title || ''}
          onChange={(e) => update({ title: e.target.value })}
          placeholder="Título"
          className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-[11px] font-medium text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-borderSubtle focus:outline-none"
        />
        <div ref={presetRef} className="relative">
          <button
            type="button"
            onClick={() => setPresetOpen((v) => !v)}
            title="Preencher com dados do plano"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-apolo-plan-accent hover:bg-apolo-plan-accentMuted"
          >
            <Icons.Sparkles size={11} /> Do plano
          </button>
          {presetOpen ? (
            <div role="menu" className="absolute right-0 top-7 z-50 w-44 overflow-hidden rounded-md border border-apolo-borderSubtle bg-apolo-surface2 shadow-lg">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p.id)}
                  className="block w-full px-3 py-1.5 text-left text-[11px] text-apolo-textPrimary hover:bg-apolo-surface3"
                >
                  {p.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setMode((m) => (m === 'data' ? 'chart' : 'data'))}
          title={mode === 'data' ? 'Ver gráfico' : 'Editar dados'}
          className="inline-flex h-6 w-6 items-center justify-center rounded text-apolo-textSecondary hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
          aria-label={mode === 'data' ? 'Ver gráfico' : 'Editar dados'}
        >
          {mode === 'data' ? <Icons.BarChart3 size={13} /> : <Icons.Table2 size={13} />}
        </button>
      </div>

      {/* Corpo */}
      <div className="min-h-0 flex-1 overflow-auto p-2">
        {mode === 'chart' ? (
          <ChartRenderer type={type} series={series} height={200} />
        ) : (
          <div className="flex flex-col gap-1">
            <table className="w-full border-collapse text-[11px]">
              <thead>
                <tr className="text-left text-apolo-textTertiary">
                  <th className="py-0.5 pr-1 font-medium">Categoria</th>
                  <th className="py-0.5 pr-1 font-medium">Valor</th>
                  <th className="w-6" />
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan={3} className="py-2 text-apolo-textTertiary">Sem dados. Adicione linhas ou use o bot&atilde;o Do plano.</td></tr>
                ) : null}
                {data.map((d, i) => (
                  <tr key={i} className="group">
                    <td className="py-0.5 pr-1">
                      <input
                        type="text" value={d.x ?? ''}
                        onChange={(e) => setPoint(i, 'x', e.target.value)}
                        placeholder="ex: Jan"
                        className="w-full rounded border border-apolo-borderSubtle bg-apolo-bg px-1 py-0.5 focus:border-apolo-plan-accentBorder focus:outline-none"
                      />
                    </td>
                    <td className="py-0.5 pr-1">
                      <input
                        type="number" value={d.y ?? ''}
                        onChange={(e) => setPoint(i, 'y', e.target.value)}
                        placeholder="0"
                        className="w-full rounded border border-apolo-borderSubtle bg-apolo-bg px-1 py-0.5 focus:border-apolo-plan-accentBorder focus:outline-none"
                      />
                    </td>
                    <td>
                      <button
                        type="button" onClick={() => removeRow(i)} aria-label="Remover linha"
                        className="invisible inline-flex h-5 w-5 items-center justify-center rounded text-apolo-textTertiary hover:text-apolo-error group-hover:visible"
                      >
                        <Icons.X size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button" onClick={addRow}
              className="mt-1 inline-flex w-fit items-center gap-1 rounded text-[11px] text-apolo-plan-accent hover:underline"
            >
              <Icons.Plus size={11} /> Adicionar ponto
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
